// Local stats-cache writes for a settled match.
//
// The server applies coins/xp/wins/losses/ladder atomically and hands back
// `newTotals`; the client mirrors those so the hub updates without waiting
// for a profile refetch. When `newTotals` is absent the client derives the
// totals itself, and that fallback is where no-contest settlements went
// wrong: a zero envelope carrying `arbitration.reason` means the server
// recorded NOTHING (settleNoContest never calls applyMatchReward), yet
// `won: false` on both seats made every client book a loss the NO CONTEST
// screen had just said didn't happen.
//
// Pure and separate from app.jsx so the arithmetic is testable without
// instantiating App. Same shape as resolveDiscardLeave.

import { neutralArbitration } from './arbitrationOutcomes';

// A settlement that is neither a win nor a loss. Keyed on the SAME table the
// result modal renders from, not on any truthy arbitration reason: the two
// must agree, or a banner and the hub counter contradict each other again. Not
// on `newTotals: null` alone either, because sandbox and practice-bot matches
// also return null totals and those DO keep their existing counters.
function isNoContest(reward) {
  return !!neutralArbitration(reward?.arbitration?.reason);
}

/**
 * Did arbitration touch this settlement at all?
 *
 * Deliberately BROADER than isNoContest, and the difference is the point. The
 * stats cache has to agree with the modal, so it keys on the neutral table.
 * The draft round report is asking a different question: "am I certain who
 * won?" Both seats get `won: false` on any zero envelope, so both would POST
 * the OPPONENT as the round winner and last write would take the round and the
 * prize card. A new server reason nobody has added to the table yet must fail
 * SAFE there, which means any arbitration reason is enough to hold the report.
 */
export function hasArbitrationReason(reward) {
  return !!reward?.arbitration?.reason;
}

/**
 * Wins/losses counters and whether to append a match-history row.
 * Server totals always win; the fallback only guesses when they are absent.
 */
export function resolveStatsCacheWrite(reward, profile) {
  const noContest = isNoContest(reward);
  const won = !!reward?.won;
  return {
    noContest,
    wins: reward?.newTotals?.wins ?? ((profile.wins || 0) + (!noContest && won ? 1 : 0)),
    losses: reward?.newTotals?.losses ?? ((profile.losses || 0) + (!noContest && !won ? 1 : 0)),
    // The server writes no match_history row for a no-contest, so a local
    // one is a phantom that disappears on the next fetch. Worse, the
    // win-streak achievement walks this list and breaks on the first
    // `won: false`, so the phantom row also snapped a live streak.
    appendHistory: !noContest,
  };
}

/**
 * The `result` blob POSTed to /instances/:id/complete.
 *
 * Both seats see `won: false` on a no-contest, so both would name the OTHER
 * player as winner and last-write-wins decided it. A null winner is already
 * a valid shape server-side (the /leave path uses it) and renders as the
 * neutral 'Completed' label in the save list.
 */
export function resolveCompletedInstanceResult(reward, myId, opponentId) {
  if (isNoContest(reward)) return { winner: null, wonByMe: false };
  const won = !!reward?.won;
  return {
    winner: won ? myId : (opponentId || null),
    wonByMe: won,
  };
}
